/**
 * Halloran's workshop on screen: Des's job card, clipped over the game. One row per job (respray, metallic,
 * wheels, stripes / roof / band, plates, the tune, body work); ← → (or the ◂ ▸ buttons) try an option on the
 * car, Enter (or BUY) pays for it, Esc (or ✕, or DONE) shuts the card and puts back anything not paid for.
 * It only draws: the rows and what they do come from the `GarageModel` the activity system hands it.
 */
import { el, esc, html, setText, toggle } from '../ui/dom';
import type { NavAction } from '../ui/screens';
import type { ModuleOverlay } from '../ui/uiTypes';

export interface GarageRow {
  id: string;
  label: string;
  /** Current option ("Pillar-box red"); rows with options cycle with ← →. */
  value?: string;
  cycles?: boolean;
  /** Price of the shown option, or null when there's nothing to buy (done already, not for this car). */
  price: number | null;
  note?: string;
}

export interface GarageModel {
  title: string;
  sub: string;
  balance(): number;
  rows(): GarageRow[];
  cycle(id: string, dir: 1 | -1): void;
  /** Pay for the row's shown option. False (and a reason in the note) when it can't be done. */
  buy(id: string): boolean;
  close(): void;
  touch(): boolean;
  sound(s: 'tick' | 'click' | 'confirm' | 'back' | 'whoosh' | 'denied'): void;
}

const pounds = (n: number): string => `£${n.toLocaleString('en-GB')}`;

export class GarageScreen implements ModuleOverlay {
  readonly root: HTMLElement;
  index = 0;
  private readonly list: HTMLElement;
  private readonly cash: HTMLElement;
  private rowEls: HTMLElement[] = [];
  private rows: { id: string }[] = [];

  constructor(parent: HTMLElement, private readonly model: GarageModel) {
    this.root = el('div', 'bh-screen gw-screen', undefined, parent);
    toggle(this.root, 'touch', model.touch());
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) {
        model.sound('back');
        model.close();
      }
    });
    const card = el('div', 'gw-card bh-paper', undefined, this.root);
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-label', model.title);
    const head = el('div', 'gw-head', undefined, card);
    html('div', 'gw-title', `${esc(model.title)}<span>${esc(model.sub)}</span>`, head);
    this.cash = el('div', 'gw-cash', '', head);
    const close = el('button', 'gw-close', '✕', head);
    close.type = 'button';
    close.setAttribute('aria-label', 'Done');
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      model.sound('back');
      model.close();
    });
    this.list = el('div', 'gw-list', undefined, card);
    this.list.dataset.touchScroll = '';
    if (!model.touch()) html('div', 'gw-foot', '<span>↑ ↓ choose</span><span>← → try it on</span><span>Enter pay</span><span>Esc done</span>', card);
    this.render();
  }

  /** Redraw from the model (after a purchase, a cycle or a balance change). */
  render(): void {
    const rows = this.model.rows();
    this.rows = rows;
    this.index = Math.max(0, Math.min(this.index, rows.length - 1));
    setText(this.cash, pounds(this.model.balance()));
    this.list.textContent = '';
    this.rowEls = rows.map((r, i) => {
      const row = el('div', 'gw-row', undefined, this.list);
      toggle(row, 'sel', i === this.index);
      // Dimmed: nothing to choose and nothing to pay for (a full tune, no dents).
      toggle(row, 'off', r.price === null && !r.cycles && r.id !== 'done');
      row.addEventListener('click', () => {
        if (this.index !== i) {
          this.index = i;
          this.model.sound('tick');
          this.render();
        }
      });
      el('div', 'gw-label', r.label, row);
      const val = el('div', 'gw-value', undefined, row);
      if (r.cycles) {
        const prev = el('button', 'gw-arrow', '◂', val);
        prev.type = 'button';
        prev.setAttribute('aria-label', `Previous ${r.label}`);
        prev.addEventListener('click', (e) => {
          e.stopPropagation();
          this.index = i;
          this.cycle(-1);
        });
        el('span', 'gw-opt', r.value ?? '', val);
        const next = el('button', 'gw-arrow', '▸', val);
        next.type = 'button';
        next.setAttribute('aria-label', `Next ${r.label}`);
        next.addEventListener('click', (e) => {
          e.stopPropagation();
          this.index = i;
          this.cycle(1);
        });
      } else el('span', 'gw-opt', r.value ?? '', val);
      el('div', 'gw-note', r.note ?? '', row);
      const buy = el('button', 'gw-buy', r.id === 'done' ? 'DONE' : r.price === null ? '—' : pounds(r.price), row);
      buy.type = 'button';
      buy.disabled = r.price === null && r.id !== 'done';
      buy.addEventListener('click', (e) => {
        e.stopPropagation();
        this.index = i;
        this.confirm();
      });
      return row;
    });
    this.rowEls[this.index]?.scrollIntoView?.({ block: 'nearest' });
  }

  private cycle(dir: 1 | -1): void {
    const r = this.rows[this.index];
    if (!r) return;
    this.model.cycle(r.id, dir);
    this.model.sound('tick');
    this.render();
  }

  private confirm(): void {
    const r = this.rows[this.index];
    if (!r) return;
    if (r.id === 'done') {
      this.model.sound('back');
      this.model.close();
      return;
    }
    const ok = this.model.buy(r.id);
    this.model.sound(ok ? 'confirm' : 'denied');
    this.render();
  }

  onNav(a: NavAction): boolean {
    switch (a) {
      case 'up':
        this.index = (this.index - 1 + this.rows.length) % this.rows.length;
        this.model.sound('tick');
        this.render();
        return true;
      case 'down':
        this.index = (this.index + 1) % this.rows.length;
        this.model.sound('tick');
        this.render();
        return true;
      case 'left':
        this.cycle(-1);
        return true;
      case 'right':
        this.cycle(1);
        return true;
      case 'confirm':
        this.confirm();
        return true;
      case 'back':
        this.model.sound('back');
        this.model.close();
        return true;
    }
    return false;
  }

  dispose(): void {
    this.root.remove();
  }
}
